/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", function () {
  const elements = document.querySelectorAll(
    ".wp-block-scd-block-animation-text",
  );

  if (elements.length === 0) {
    return;
  }

  elements.forEach(async (element) => {
    const animationType = element.getAttribute("data-animation-type");

    if (animationType === "none") return;

    switch (animationType) {
      case "1":
        gsap.fromTo(
          element,
          {
            backgroundPosition: "100% 0%",
          },
          {
            backgroundPosition: "0% 0%",
            scrollTrigger: {
              trigger: element,
              start: "top 80%",
              end: "top top",
              scrub: true,
            },
          },
        );
        break;
      case "2":
        //elementの子要素を1行ずつspanで囲む
        wrapTextInSpans(element);

        const children = Array.from(element.children);
        children.forEach((child) => {
          gsap.fromTo(
            child,
            {
              backgroundPosition: "100% 0%",
            },
            {
              backgroundPosition: "0% 0%",
              scrollTrigger: {
                trigger: child,
                start: "top 80%",
                end: "bottom 80%",
                scrub: true,
              },
            },
          );
        });

        break;
      case "3":
        break;
      default:
        break;
    }
  });

  /**
   * 要素内のテキストを1行ずつspanで囲む関数
   * 親要素の幅に応じて自動的に行を判定する
   * <br>タグは同じ位置に保持される
   *
   * @param {HTMLElement} element - テキストを含む要素
   */
  function wrapTextInSpans(element) {
    // 既存の子ノードを順番に取得（順序を保持）
    const childNodes = Array.from(element.childNodes);

    // 要素の幅を取得（paddingやborderを考慮）
    const computedStyle = window.getComputedStyle(element);
    const elementWidth =
      element.offsetWidth -
      parseFloat(computedStyle.paddingLeft) -
      parseFloat(computedStyle.paddingRight);

    // 一時的な測定用のspanを作成
    const measureSpan = document.createElement("span");
    measureSpan.style.position = "absolute";
    measureSpan.style.visibility = "hidden";
    measureSpan.style.whiteSpace = "nowrap";
    measureSpan.style.fontSize = computedStyle.fontSize;
    measureSpan.style.fontFamily = computedStyle.fontFamily;
    measureSpan.style.fontWeight = computedStyle.fontWeight;
    measureSpan.style.fontStyle = computedStyle.fontStyle;
    measureSpan.style.letterSpacing = computedStyle.letterSpacing;
    element.appendChild(measureSpan);

    // 既存の内容をクリア
    const fragment = document.createDocumentFragment();
    let currentLine = "";
    let currentLineWidth = 0;

    // 子ノードを順番に処理
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // テキストノードの場合
        const text = node.textContent;

        // テキストを1文字ずつ処理
        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const isNewline = char === "\n" || char === "\r";

          if (isNewline) {
            // 改行文字の場合は現在の行をspanで囲んで追加
            if (currentLine.trim()) {
              const span = document.createElement("span");
              span.textContent = currentLine;
              fragment.appendChild(span);
            }
            currentLine = "";
            currentLineWidth = 0;
            continue;
          }

          // 現在の行に文字を追加した場合の幅を測定
          measureSpan.textContent = currentLine + char;
          const newWidth = measureSpan.offsetWidth;

          // 要素の幅を超える場合は現在の行をspanで囲んで追加し、新しい行を開始
          if (newWidth > elementWidth && currentLine.length > 0) {
            const span = document.createElement("span");
            span.textContent = currentLine;
            fragment.appendChild(span);
            currentLine = char;
            currentLineWidth = measureSpan.offsetWidth;
          } else {
            currentLine += char;
            currentLineWidth = newWidth;
          }
        }
      } else if (node.nodeName.toLowerCase() === "br") {
        // <br>タグの場合は現在の行をspanで囲んで追加し、<br>タグを追加
        if (currentLine.trim()) {
          const span = document.createElement("span");
          span.textContent = currentLine;
          fragment.appendChild(span);
        }
        // <br>タグをクローンして追加
        fragment.appendChild(node.cloneNode(true));
        currentLine = "";
        currentLineWidth = 0;
      } else {
        // その他の要素ノードの場合
        // 現在の行をspanで囲んで追加
        if (currentLine.trim()) {
          const span = document.createElement("span");
          span.textContent = currentLine;
          fragment.appendChild(span);
          currentLine = "";
          currentLineWidth = 0;
        }
        // 要素ノードをそのまま追加
        fragment.appendChild(node.cloneNode(true));
      }
    });

    // 最後の行を追加
    if (currentLine.trim()) {
      const span = document.createElement("span");
      span.textContent = currentLine;
      fragment.appendChild(span);
    }

    // 測定用のspanを削除
    element.removeChild(measureSpan);

    // 既存の内容をクリアして、処理済みの内容を追加
    element.innerHTML = "";
    element.appendChild(fragment);
  }
});
